import React, { useCallback, useMemo, useState } from 'react'
import Flex from 'antd/lib/flex'
import Menu from 'antd/lib/menu'
import Space from 'antd/lib/space'
import Typography from 'antd/lib/typography'
import { useAtom } from 'jotai'
import cn from 'classnames'
import groupBy from 'lodash/groupBy'
import Modal, { hide } from '../Modal'
import CoreButton from '../CoreButton'
import { useTheme } from '../ThemeProvider/ThemeProvider'
import { SCRIPT_TYPES } from '../../../constants/constants'
import { removeHtmlTags } from '../../../common/editor-utils'
import * as atoms from '../ProjectNavigation/atoms'
import { upsertInfo } from '../../../services/api/script'
import styles from './FreePlanScriptSelector.module.scss'
import MaterialIcon from '../MaterialIcon'

const { Text } = Typography

const FreePlanScriptSelector = () => {
  const { themeMode } = useTheme()
  const [scripts] = useAtom(atoms.allScripts)
  const [, setDefaultScriptUuid] = useAtom(atoms.setDefaultScriptUuid)
  const [selectedUuid, setSelectedUuid] = useState(null)

  const items = useMemo(() => {
    // Group scripts by project_name; use 'No project' when undefined
    const groups = groupBy(scripts, (s) => s?.project_name || 'No project')
    return Object.entries(groups).map(([projectName, groupScripts]) => ({
      label: projectName,
      icon: <MaterialIcon outlined name='folder_open' />,
      key: `group-${projectName}`,
      children: groupScripts.map((script) => {
        const scriptType = SCRIPT_TYPES[script?.data?.type || 'featureFilm']
        return {
          label: removeHtmlTags(script?.data?.title),
          icon: scriptType?.icon,
          key: script.uuid
        }
      })
    }))
  }, [scripts])

  const selectedKeys = useMemo(() => selectedUuid ? [selectedUuid] : [], [selectedUuid])

  const handleSelect = useCallback((info) => {
    setSelectedUuid(info.key)
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      const script = scripts.find(s => s.uuid === selectedUuid)
      if (!script) return
      // Persist info: include existing data and ensure default flag present (explicit false per requirements)
      await upsertInfo({ scriptUuid: script.uuid, data: { ...(script.data || {}), default: true } })
      setDefaultScriptUuid(script.uuid)
      hide('freePlanScriptSelector')
    } catch (e) {
      console.warn(e)
    }
  }, [scripts, selectedUuid, setDefaultScriptUuid])

  return (
    <Modal
      id='freePlanScriptSelector'
      title='Select a Script'
      themeMode={themeMode}
      hasCloseButton={false}
      shouldCloseOnOverlayClick={false}
      bodyStyle={{ margin: 0, width: '100%' }}
      style={{ maxWidth: '640px' }}
    >
      <div className={styles.contentWrapper}>
        <Space direction='vertical' size='small' style={{ width: '100%' }}>
          <Text strong>
            You’re on the Free Plan. Please choose the script you’d like to keep working on.
          </Text>
          {items.length > 0
            ? (
              <>
                <div className={cn(styles.menuContainer, styles[themeMode])}>
                  <Menu
                    inlineIndent={8}
                    className={cn('ai-video-navigation', themeMode)}
                    mode='inline'
                    onSelect={handleSelect}
                    defaultOpenKeys={items.map(i => i.key)}
                    selectedKeys={selectedKeys}
                    items={items}
                  />
                </div>
                <Flex justify='end'>
                  <CoreButton
                    mode='primary'
                    size='small'
                    disabled={!selectedUuid}
                    onClick={handleConfirm}
                  >
                    Select
                  </CoreButton>
                </Flex>
              </>
              )
            : (
              <Text type='secondary'>No scripts found.</Text>
              )}
        </Space>
      </div>
    </Modal>
  )
}

export default FreePlanScriptSelector
